import { useState } from 'react'

type Period = 'month' | 'quarter' | 'year' | 'custom'

const PROFILE: Record<
  string,
  {
    live: number
    progress: number
    share: number
    onTime: number
    days: number
    overdue: number
    profit: number
    history: number[]
  }
> = {
  northstar: {
    live: 14,
    progress: 5,
    share: 18.4,
    onTime: 92,
    days: 27,
    overdue: 0,
    profit: 42800,
    history: [78, 88, 84, 94, 90, 92],
  },
  aster: {
    live: 8,
    progress: 4,
    share: 11.7,
    onTime: 76,
    days: 34,
    overdue: 12400,
    profit: 31600,
    history: [82, 74, 79, 71, 80, 76],
  },
  hearth: {
    live: 17,
    progress: 3,
    share: 14.2,
    onTime: 97,
    days: 13,
    overdue: 0,
    profit: 28700,
    history: [93, 96, 94, 98, 96, 97],
  },
}

const multiplier: Record<Exclude<Period, 'custom'>, number> = {
  month: 0.085,
  quarter: 0.25,
  year: 1,
}
const PERIOD_IDS = ['oldest', 'older', 'prior-3', 'prior-2', 'prior', 'current']
const usd = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`

export function ClientPosition({
  id,
  name,
  initials,
  terms,
  credit,
  creditUsed,
  annualValue,
}: {
  id: string
  name: string
  initials: string
  terms: string
  credit: number
  creditUsed: number
  annualValue: number
}) {
  const [period, setPeriod] = useState<Period>('year')
  const [from, setFrom] = useState('2026-01-01')
  const [to, setTo] = useState('2026-10-05')
  const profile = PROFILE[id] ?? PROFILE.northstar
  const scale = period === 'custom' ? 0.75 : multiplier[period]
  const position = {
    volume: Math.max(1, Math.round(profile.live * scale)),
    progress: Math.max(1, Math.round(profile.progress * Math.max(scale, 0.25))),
    revenue: annualValue * scale,
    profit: profile.profit * scale,
  }
  const available = credit - creditUsed
  const usedPct = Math.round((creditUsed / credit) * 100)
  const creditTone = profile.overdue > 0 || usedPct >= 75 ? 'watch' : 'good'
  const creditLabel = profile.overdue > 0 ? 'Review' : usedPct >= 75 ? 'Watch' : 'Healthy'

  return (
    <section className="clp">
      <header>
        <div className="clp-id">
          <i>{initials}</i>
          <div>
            <strong>{name}</strong>
            <span>Payment terms {terms} · Active account</span>
          </div>
        </div>
        <fieldset className="clp-period">
          <legend>Client reporting period</legend>
          {(['month', 'quarter', 'year', 'custom'] as Period[]).map((item) => (
            <button
              type="button"
              className={period === item ? 'active' : ''}
              onClick={() => setPeriod(item)}
              key={item}
            >
              {item === 'quarter' ? 'Quarter' : item[0]?.toUpperCase() + item.slice(1)}
            </button>
          ))}
        </fieldset>
        {period === 'custom' && (
          <div className="clp-dates">
            <label>
              From
              <input
                aria-label="Client period from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label>
              To
              <input
                aria-label="Client period to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
          </div>
        )}
      </header>

      <div className="clp-body">
        <div className="clp-general">
          <div className="clp-title">
            <span>General position</span>
            <small>{period === 'custom' ? `${from} to ${to}` : `Current ${period}`}</small>
          </div>
          <div className="clp-kpis">
            <Kpi label="Live references" value={`${position.volume}`} note="active shipments" />
            <Kpi label="In progress" value={`${position.progress}`} note="quotes + bookings" />
            <Kpi label="Share of volume" value={`${profile.share}%`} note="of company TEU" />
            <Kpi label="Revenue" value={usd(position.revenue)} note="customer sell" />
            <Kpi
              label="Gross profit"
              value={usd(position.profit)}
              note={`${((position.profit / position.revenue) * 100).toFixed(1)}% contribution`}
              good
            />
          </div>
        </div>

        <div className="clp-payment">
          <div className="clp-title">
            <span>Payment behaviour</span>
            <small>last 6 periods</small>
          </div>
          <div className="clp-paygrid">
            <Kpi
              label="Paid on time"
              value={`${profile.onTime}%`}
              note="of settled invoices"
              good={profile.onTime >= 90}
            />
            <Kpi label="Average payment" value={`${profile.days}d`} note={`terms ${terms}`} />
            <Kpi
              label="Overdue"
              value={usd(profile.overdue)}
              note={profile.overdue ? 'requires attention' : 'nothing outstanding'}
              warn={profile.overdue > 0}
            />
          </div>
          <div className="clp-bars" aria-label="Payment history">
            {profile.history.map((value, index) => (
              <i
                key={PERIOD_IDS[index]}
                style={{ height: `${value}%` }}
                title={`${value}% paid on time`}
              />
            ))}
          </div>
        </div>

        <aside className={`clp-credit ${creditTone}`}>
          <div>
            <span>Credit status</span>
            <b>{creditLabel}</b>
          </div>
          <strong>{usd(available)}</strong>
          <small>available of {usd(credit)}</small>
          <div className="clp-creditbar">
            <i style={{ width: `${Math.min(usedPct, 100)}%` }} />
          </div>
          <p>
            <b>{usedPct}% used</b>
            <span>{usd(creditUsed)} exposure</span>
          </p>
          {profile.overdue > 0 && <em>{usd(profile.overdue)} overdue</em>}
          <button type="button">View credit account →</button>
        </aside>
      </div>
      <style>{styles}</style>
    </section>
  )
}

function Kpi({
  label,
  value,
  note,
  good = false,
  warn = false,
}: { label: string; value: string; note: string; good?: boolean; warn?: boolean }) {
  return (
    <div className="clp-kpi">
      <span>{label}</span>
      <strong className={good ? 'good' : warn ? 'warn' : ''}>{value}</strong>
      <small>{note}</small>
    </div>
  )
}

const styles = `
.clp{border:1px solid var(--border);background:var(--surface-0);border-radius:15px;margin-top:18px;overflow:hidden;box-shadow:var(--shadow-card)}.clp *{box-sizing:border-box}.clp button,.clp input{font:inherit}.clp>header{display:flex;align-items:center;gap:16px;padding:13px 15px;border-bottom:1px solid var(--border)}.clp-id{display:flex;align-items:center;gap:10px;margin-right:auto}.clp-id>i{width:36px;height:36px;border-radius:10px;background:color-mix(in srgb,var(--frd-accent) 12%,var(--surface-1));color:var(--frd-accent);font-family:var(--mono,ui-monospace,monospace);font-style:normal;font-weight:750;display:grid;place-items:center}.clp-id strong,.clp-id span{display:block}.clp-id span{font-size:9px;color:var(--text-muted);margin-top:2px}.clp-period{display:flex;border:1px solid var(--border);border-radius:9px;overflow:hidden}.clp-period button{border:0;border-right:1px solid var(--border);background:transparent;color:var(--text-muted);padding:7px 9px;font-size:8.5px}.clp-period button:last-child{border:0}.clp-period button.active{background:var(--text-primary);color:var(--surface-0)}.clp-dates{display:flex;gap:6px}.clp-dates label{font-size:7px;color:var(--text-muted);text-transform:uppercase}.clp-dates input{display:block;border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:7px;padding:5px;font-size:8px;margin-top:2px}.clp-body{display:grid;grid-template-columns:1.55fr 1fr 230px}.clp-body>div,.clp-body>aside{padding:14px 16px;border-right:1px solid var(--border)}.clp-body>aside{border:0}.clp-title{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px}.clp-title span,.clp-credit>div>span{font-family:var(--mono,ui-monospace,monospace);font-size:8px;letter-spacing:.1em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.clp-title small{font-size:8px;color:var(--text-muted)}.clp-kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:12px}.clp-kpi span,.clp-kpi small{display:block;color:var(--text-muted);font-size:8px}.clp-kpi strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:15px;margin:4px 0 2px}.clp-kpi strong.good{color:#047857}.clp-kpi strong.warn{color:#b45309}.clp-paygrid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}.clp-bars{height:28px;display:flex;align-items:end;gap:4px;margin-top:10px;border-bottom:1px solid var(--border)}.clp-bars i{display:block;flex:1;background:color-mix(in srgb,var(--frd-accent) 55%,var(--surface-1));border-radius:2px 2px 0 0;min-height:4px}.clp-credit{position:relative}.clp-credit>div:first-child{display:flex;justify-content:space-between;align-items:center}.clp-credit>div:first-child b{font-size:8px;padding:3px 6px;border-radius:10px;background:#10b98118;color:#047857}.clp-credit.watch>div:first-child b{background:#f59e0b18;color:#b45309}.clp-credit>strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:20px;margin-top:9px}.clp-credit>small{font-size:8px;color:var(--text-muted)}.clp-creditbar{height:5px;background:var(--surface-1);border-radius:5px;overflow:hidden;margin-top:9px}.clp-creditbar i{display:block;height:100%;background:#10b981}.clp-credit.watch .clp-creditbar i{background:#f59e0b}.clp-credit p{display:flex;justify-content:space-between;font-size:8px;margin:5px 0}.clp-credit p span{color:var(--text-muted)}.clp-credit em{display:block;font-style:normal;color:#b45309;font-size:8px;font-weight:700}.clp-credit button{border:0;background:none;color:var(--frd-accent);font-size:8.5px;font-weight:700;padding:8px 0 0}
.clp-period{margin:0;padding:0}.clp-period legend{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)}
@media(max-width:1000px){.clp-body{grid-template-columns:1fr 1fr}.clp-credit{grid-column:1/-1;border-top:1px solid var(--border)!important}.clp-kpis{grid-template-columns:repeat(3,1fr)}}
@media(max-width:760px){.clp>header{align-items:stretch;flex-direction:column}.clp-period{width:100%}.clp-period button{flex:1}.clp-dates{display:grid;grid-template-columns:1fr 1fr}.clp-dates input{width:100%}.clp-body{grid-template-columns:1fr}.clp-body>div,.clp-body>aside{border-right:0;border-bottom:1px solid var(--border)}.clp-kpis{grid-template-columns:repeat(2,1fr)}.clp-paygrid{grid-template-columns:repeat(3,1fr)}.clp-credit{grid-column:auto}}
`
